// Notification Service - Handles browser push notifications

// The shared axios instance, not a bare fetch: /api/v1/notifications/* now
// requires a token, and only this client attaches one.
import { api } from './api';


const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

class NotificationService {
  private swRegistration: ServiceWorkerRegistration | null = null;

  // Initialize service worker for push notifications
  async initPushNotifications(): Promise<boolean> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.log('Push notifications not supported');
      return false;
    }

    try {
      this.swRegistration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered');
      return true;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return false;
    }
  }

  // Request permission for browser push notifications
  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      return 'denied';
    }

    const permission = await Notification.requestPermission();

    if (permission === 'granted' && this.swRegistration) {
      await this.subscribeToPush();
    }

    return permission;
  }

  // Subscribe to push notifications
  private async subscribeToPush(): Promise<PushSubscription | null> {
    if (!this.swRegistration || !VAPID_PUBLIC_KEY) return null;

    try {
      const subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
      });

      // Send subscription to backend
      const json = subscription.toJSON();
      await api.post('/notifications/subscribe', {
        endpoint: json.endpoint,
        p256dh: json.keys?.p256dh,
        auth: json.keys?.auth,
        user_agent: navigator.userAgent,
      });

      return subscription;
    } catch (error) {
      console.error('Failed to subscribe to push:', error);
      return null;
    }
  }

  // Get notification icon based on alert type
  getAlertIcon(alertType: string): string {
    const icons: Record<string, string> = {
      payment_due_soon: '💳',
      payment_overdue: '🚨',
      moh_permit_expiring: '🏥',
      mot_permit_expiring: '📦',
      contract_expiring: '📝',
      permit_coverage_gap: '⚠️',
      shipment_eta_today: '🚢',
      shelf_life_violation: '⏰',
      po_confirmed: '✅',
      po_in_production: '🏭',
      po_ready_to_ship: '📦',
      po_shipped: '🚢',
      po_approaching_aqaba: '⚓',
      po_approaching_trebel: '🏁',
      po_delivered: '📬',
    };
    return icons[alertType] || '🔔';
  }

  // Get severity color class
  getSeverityColor(severity: string): string {
    const colors: Record<string, string> = {
      Info: 'bg-blue-100 text-blue-800',
      Warning: 'bg-amber-100 text-amber-800',
      Critical: 'bg-red-100 text-red-800',
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  }

  // Helper to convert VAPID key
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

export const notificationService = new NotificationService();

/**
 * Perform alert actions the service worker cannot.
 *
 * A push notification's Acknowledge/Snooze buttons are handled in sw.js, which
 * has no access to localStorage and therefore no token. It posts the action
 * here instead, and this runs it with the signed-in user's own credentials.
 * Registered once at module load, so any open tab can service the request.
 */
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    const data = event.data;
    if (!data || data.type !== 'ALERT_ACTION' || !data.alertId) return;

    const run =
      data.action === 'snooze'
        ? api.post(`/alerts/${data.alertId}/snooze`, { duration: data.duration ?? '1h' })
        : api.post(`/alerts/${data.alertId}/acknowledge`);

    run.catch((err) => console.error(`Alert ${data.action} failed:`, err));
  });
}

export default notificationService;
